/**
 * Formatting for the AI spend dashboard.
 *
 * Two rules this module exists to enforce:
 *
 * 1. **Money arrives from the API as a number in USD and is formatted only at
 *    render time.** Nothing here parses a rendered string back into a number,
 *    and nothing outside here prints a `$`. Deltas are computed from the raw
 *    values a component is given separately, never from what it displays.
 * 2. **Quantities are never summed across billing units.** Tokens, characters,
 *    audio seconds, videos and session seconds are different things; money is
 *    the only figure comparable between providers.
 */

export const UNIT_META = {
  tokens: { label: 'tokens', short: 'tok', kind: 'count', per: 1000, perLabel: '1K tokens' },
  characters: { label: 'characters', short: 'chars', kind: 'count', per: 1000, perLabel: '1K chars' },
  audio_seconds: { label: 'audio', short: 'audio', kind: 'duration', per: 60, perLabel: 'minute' },
  session_seconds: { label: 'session', short: 'session', kind: 'duration', per: 60, perLabel: 'minute' },
  videos: { label: 'videos', short: 'vid', kind: 'count', per: 1, perLabel: 'video' },
};

export const isBlank = (value) => value === null || value === undefined || value === '';

/**
 * USD as a string. Sub-cent costs keep more decimals so a cheap call never
 * renders as "$0.00" and looks free.
 */
export const formatMoney = (value, { compact = false } = {}) => {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (compact && abs >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  const digits = abs === 0 ? 2 : abs < 0.01 ? 4 : abs < 1 ? 3 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
};

/** Full precision, for the run detail panel where the exact figure matters. */
export const formatMoneyPrecise = (value) => {
  if (!Number.isFinite(value)) return '—';
  return `$${value.toFixed(6)}`;
};

export const formatCount = (value) =>
  Number.isFinite(value) ? new Intl.NumberFormat('en-US').format(value) : '—';

export const formatCompact = (value) => {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return formatCount(value);
};

/** Input is a 0..1 share, not 0..100. */
export const formatPercent = (ratioValue, digits = 1) => {
  if (!Number.isFinite(ratioValue)) return '—';
  return `${(ratioValue * 100).toFixed(digits)}%`;
};

export const formatLatency = (ms) => {
  if (!Number.isFinite(ms)) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${Math.round(ms)} ms`;
};

export const formatDurationSeconds = (seconds) => {
  if (!Number.isFinite(seconds)) return '—';
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m ${String(rest).padStart(2, '0')}s`;
  return `${rest}s`;
};

const formatQuantity = (unit, quantity) => {
  const meta = UNIT_META[unit];
  if (!meta) return `${formatCount(quantity)} ${unit}`;
  if (meta.kind === 'duration') return `${formatDurationSeconds(quantity)} ${meta.label}`;
  return `${formatCompact(quantity)} ${meta.label}`;
};

/**
 * `[{unit, quantity}]` -> "1.2M tokens · 41 videos".
 *
 * A row spanning several units prints them side by side rather than adding
 * them up, which is why no table here has a "Tokens" column for a HeyGen row
 * to sit under.
 */
export const formatUsage = (usage) => {
  if (!Array.isArray(usage) || usage.length === 0) return '—';
  return usage.map(({ unit, quantity }) => formatQuantity(unit, quantity)).join(' · ');
};

/** "$0.021 / 1K chars", or a dash when the row mixes units. */
export const formatUnitCost = (unitCostUsd, unit) => {
  if (!Number.isFinite(unitCostUsd) || !unit || unit === 'mixed') return '—';
  const meta = UNIT_META[unit];
  if (!meta) return '—';
  return `${formatMoney(unitCostUsd * meta.per)} / ${meta.perLabel}`;
};

/**
 * The delta between two raw numbers, or null when there is nothing meaningful
 * to compare. Never call this with anything that has been through a formatter.
 */
export const deltaRatio = (current, previous) => {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) {
    return null;
  }
  return (current - previous) / Math.abs(previous);
};

export const formatDelta = (ratioValue) => {
  if (ratioValue === null || !Number.isFinite(ratioValue)) return null;
  const sign = ratioValue >= 0 ? '+' : '−';
  return `${sign}${(Math.abs(ratioValue) * 100).toFixed(1)}%`;
};

/** '2026-08-01' -> local midnight. Never `new Date(string)`, which parses as UTC. */
export const parseCalendarDate = (value) => {
  const [year, month, day] = String(value).split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** A Date -> 'YYYY-MM-DD' using local parts, so the day never shifts. */
export const toCalendarDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** An instant, rendered in the timezone the server reports its days in. */
export const formatInstant = (iso, timeZone) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch (error) {
    return new Date(iso).toLocaleString();
  }
};

export const PRICING_STATUS_LABELS = {
  priced: 'Priced',
  estimated: 'Estimated rate',
  upper_bound: 'Upper bound',
  unknown_model: 'No price',
  unknown_quantity: 'Quantity unknown',
  not_billable: 'Not billable',
};

/** True when a figure should be shown with a qualifier rather than as fact. */
export const isApproximate = (pricingStatus) =>
  pricingStatus === 'estimated' || pricingStatus === 'upper_bound';
